import { supabase } from '../lib/supabase';

/**
 * Outcome Tracking Service
 * Records real-world performance data from drills, roleplays, and live calls
 * Enables continuous learning and response quality improvement
 */

/**
 * Record outcome from drill completion
 * @param {Object} outcomeData - Outcome data
 * @returns {Promise<Object>} - Created outcome record
 */
export const recordDrillOutcome = async (outcomeData) => {
  const {
    responseUnitId,
    userId,
    drillId,
    scores, // { clarity, confidence, control, close, overall }
    userResponse,
    timeTakenSeconds,
    context // { persona, stage, industry, drillType }
  } = outcomeData;

  try {
    const outcome = {
      response_unit_id: responseUnitId,
      user_id: userId,
      session_type: 'drill',
      session_id: drillId,
      
      // 4C Scores
      clarity_score: scores.clarity,
      confidence_score: scores.confidence,
      control_score: scores.control,
      close_score: scores.close,
      overall_score: scores.overall,
      
      // Outcome classification
      outcome_label: classifyOutcome(scores.overall),
      success_threshold: 70,
      
      // Timing
      time_to_response: timeTakenSeconds,
      response_length: userResponse?.length || 0,
      
      // Context
      context_data: context || {}
    };

    const { data, error } = await supabase
      .from('response_outcomes')
      .insert(outcome)
      .select()
      .single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error recording drill outcome:', error);
    throw error;
  }
};

/**
 * Record outcome from roleplay session
 * @param {Object} outcomeData - Outcome data
 * @returns {Promise<Object>} - Created outcome record
 */
export const recordRoleplayOutcome = async (outcomeData) => {
  const {
    responseUnitId,
    userId,
    roleplayId,
    scores,
    userResponse,
    timeTakenSeconds,
    context
  } = outcomeData;

  try {
    const outcome = {
      response_unit_id: responseUnitId,
      user_id: userId,
      session_type: 'roleplay',
      session_id: roleplayId,
      
      clarity_score: scores.clarity,
      confidence_score: scores.confidence,
      control_score: scores.control,
      close_score: scores.close,
      overall_score: scores.overall,
      
      outcome_label: classifyOutcome(scores.overall),
      success_threshold: 75, // Slightly higher for roleplays
      
      time_to_response: timeTakenSeconds,
      response_length: userResponse?.length || 0,
      
      context_data: context || {}
    };

    const { data, error } = await supabase
      .from('response_outcomes')
      .insert(outcome)
      .select()
      .single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error recording roleplay outcome:', error);
    throw error;
  }
};

/**
 * Record outcome from call debrief
 * @param {Object} outcomeData - Outcome data
 * @returns {Promise<Object>} - Created outcome record
 */
export const recordDebriefOutcome = async (outcomeData) => {
  const {
    responseUnitId,
    userId,
    callId,
    scores,
    userRating,
    userFeedback,
    context
  } = outcomeData;

  try {
    const outcome = {
      response_unit_id: responseUnitId,
      user_id: userId,
      session_type: 'debrief',
      session_id: callId,
      
      clarity_score: scores?.clarity,
      confidence_score: scores?.confidence,
      control_score: scores?.control,
      close_score: scores?.close,
      overall_score: scores?.overall,
      
      outcome_label: classifyOutcome(scores?.overall || 0),
      success_threshold: 70,
      
      user_rating: userRating,
      user_feedback: userFeedback,
      
      context_data: context || {}
    };

    const { data, error } = await supabase
      .from('response_outcomes')
      .insert(outcome)
      .select()
      .single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error recording debrief outcome:', error);
    throw error;
  }
};

/**
 * Classify outcome based on overall score
 * @param {number} overallScore - Overall score (0-100)
 * @returns {string} - Outcome label
 */
const classifyOutcome = (overallScore) => {
  if (overallScore >= 80) return 'success';
  if (overallScore >= 60) return 'partial_success';
  if (overallScore > 0) return 'fail';
  return 'abandoned';
};

/**
 * Get performance stats for a response unit
 * @param {string} responseUnitId - Response unit ID
 * @returns {Promise<Object>} - Performance statistics
 */
export const getResponsePerformance = async (responseUnitId) => {
  try {
    const { data, error } = await supabase
      .from('response_performance_stats')
      .select('*')
      .eq('response_unit_id', responseUnitId)
      .single();

    if (error && error.code !== 'PGRST116') throw error;

    return data || {
      response_unit_id: responseUnitId,
      total_uses: 0,
      success_rate: 0,
      avg_score: 0
    };
  } catch (error) {
    console.error('Error getting response performance:', error);
    throw error;
  }
};

/**
 * Get top-performing responses
 * @param {Object} filters - Filter options
 * @returns {Promise<Array>} - Top performing responses
 */
export const getTopPerformers = async (filters = {}) => {
  const {
    type = null,
    minUses = 5,
    limit = 10
  } = filters;

  try {
    const { data, error } = await supabase.rpc('get_top_performing_responses', {
      min_uses: minUses,
      match_count: limit,
      response_type: type
    });

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('Error getting top performers:', error);
    throw error;
  }
};

/**
 * Get low-performing responses (need review)
 * @param {Object} filters - Filter options
 * @returns {Promise<Array>} - Low performing responses
 */
export const getLowPerformers = async (filters = {}) => {
  const {
    type = null,
    minUses = 5,
    maxSuccessRate = 50,
    limit = 10
  } = filters;

  try {
    const { data, error } = await supabase
      .from('response_performance_stats')
      .select(`
        response_unit_id,
        success_rate,
        total_uses,
        avg_score,
        response_units (
          id,
          title,
          type
        )
      `)
      .gte('total_uses', minUses)
      .lte('success_rate', maxSuccessRate)
      .order('success_rate', { ascending: true })
      .limit(limit);

    if (error) throw error;

    // Filter by type if specified
    return type 
      ? data.filter(item => item.response_units?.type === type)
      : data;
  } catch (error) {
    console.error('Error getting low performers:', error);
    throw error;
  }
};

/**
 * Get performance breakdown by context
 * @param {string} responseUnitId - Response unit ID
 * @param {string} contextKey - Context key (persona, stage, industry)
 * @returns {Promise<Array>} - Performance by context value
 */
export const getPerformanceByContext = async (responseUnitId, contextKey) => {
  try {
    const { data, error } = await supabase
      .from('response_outcomes')
      .select('context_data, overall_score, outcome_label')
      .eq('response_unit_id', responseUnitId)
      .not('context_data', 'is', null);

    if (error) throw error;

    // Group by context value
    const grouped = {};
    data.forEach(outcome => {
      const contextValue = outcome.context_data?.[contextKey] || 'unknown';
      if (!grouped[contextValue]) {
        grouped[contextValue] = {
          contextValue,
          total: 0,
          successCount: 0,
          avgScore: 0,
          scores: []
        };
      }
      
      grouped[contextValue].total++;
      if (outcome.outcome_label === 'success') {
        grouped[contextValue].successCount++;
      }
      grouped[contextValue].scores.push(outcome.overall_score);
    });

    // Calculate averages
    const results = Object.values(grouped).map(group => ({
      contextValue: group.contextValue,
      total: group.total,
      successRate: Math.round((group.successCount / group.total) * 100),
      avgScore: Math.round(group.scores.reduce((a, b) => a + b, 0) / group.scores.length)
    }));

    // Sort by total uses
    results.sort((a, b) => b.total - a.total);

    return results;
  } catch (error) {
    console.error('Error getting performance by context:', error);
    throw error;
  }
};

/**
 * Refresh performance statistics materialized view
 * @returns {Promise<void>}
 */
export const refreshPerformanceStats = async () => {
  try {
    await supabase.rpc('refresh_response_performance_stats');
  } catch (error) {
    console.error('Error refreshing performance stats:', error);
    throw error;
  }
};

export default {
  recordDrillOutcome,
  recordRoleplayOutcome,
  recordDebriefOutcome,
  getResponsePerformance,
  getTopPerformers,
  getLowPerformers,
  getPerformanceByContext,
  refreshPerformanceStats
};
